import { REACT_APP_API_ROOT_URI } from '../../../App';
import { apiFetch } from '../../utils';

/**
 * Parameters accepted by {@link generateApplicationToken}.
 *
 * Exactly one of `lifetimeHours` or `lifetimeDays` should be supplied when a
 * finite lifetime is desired. Omitting both will rely on the backend's default
 * expiry behaviour.
 */
export interface IGenerateTokenParams {
  /** The Rentsync user ID for whom the token should be issued. */
  userId: string;
  /** Token lifetime expressed in hours (mutually exclusive with `lifetimeDays`). */
  lifetimeHours?: string;
  /** Token lifetime expressed in days (mutually exclusive with `lifetimeHours`). */
  lifetimeDays?: string;
}

/** Shape of the JSON body returned by the `/developer/create_application_token` endpoint. */
interface IGenerateTokenResponse {
  data: string;
}

/**
 * Calls the backend `/developer/create_application_token` endpoint.
 * Returns the generated token string on success, or null on failure.
 */
export const generateApplicationToken = async ({
  userId,
  lifetimeHours,
  lifetimeDays,
}: IGenerateTokenParams): Promise<string | null> => {
  const url = `${REACT_APP_API_ROOT_URI}/developer/create_application_token`;
  const body: Record<string, string> = { userId };
  if (lifetimeHours) body.lifetimeHours = lifetimeHours;
  if (lifetimeDays) body.lifetimeDays = lifetimeDays;

  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) return null;

  const json: IGenerateTokenResponse = await response.json();
  return json.data ?? null;
};
